import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, Trash2, RotateCcw, Loader2 } from "lucide-react";

export default function ContentEditor() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [wifiSsid, setWifiSsid] = useState("");
  const [wifiPwd, setWifiPwd] = useState("");
  const [sectionOverrides, setSectionOverrides] = useState({});
  const [extras, setExtras] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (user.role !== "admin") { nav("/admin"); return; }
    http.get("/api/admin/content").then((res) => {
      setData(res.data);
      const o = res.data.overrides || {};
      setWifiSsid(o.wifi_ssid || "");
      setWifiPwd(o.wifi_password || "");
      setSectionOverrides(o.section_overrides || {});
      setExtras(o.extra_local_recs || []);
    }).catch(() => toast.error("Failed to load content"));
  }, [user]);

  const sections = data?.defaults?.sections || [];
  const property = data?.defaults?.property || {};

  const grouped = useMemo(() => {
    const g = {};
    for (const s of sections) (g[s.group] ||= []).push(s);
    return g;
  }, [sections]);

  function setBody(id, body) {
    setSectionOverrides((cur) => ({ ...cur, [id]: body }));
  }
  function resetSection(id) {
    setSectionOverrides((cur) => { const c = { ...cur }; delete c[id]; return c; });
    toast.success("Reverted to default");
  }
  function addExtra() { setExtras((c) => [...c, { title: "", body: "" }]); }
  function updExtra(i, k, v) { setExtras((c) => c.map((x, ix) => ix === i ? { ...x, [k]: v } : x)); }
  function delExtra(i) { setExtras((c) => c.filter((_, ix) => ix !== i)); }

  async function save() {
    setSaving(true);
    try {
      const payload = {
        wifi_ssid: wifiSsid.trim() || null,
        wifi_password: wifiPwd.trim() || null,
        section_overrides: Object.fromEntries(Object.entries(sectionOverrides).filter(([, v]) => v && v.trim())),
        extra_local_recs: extras.filter((e) => e.title.trim() && e.body.trim()),
      };
      await http.put("/api/admin/content", payload);
      toast.success("Saved — changes are live on the guide immediately.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  if (!data) return <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center text-[#0E2A47] font-body">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#0E2A47]/8">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4 flex-wrap">
          <Link to="/admin" data-testid="content-back" className="inline-flex items-center gap-1.5 text-sm text-[#0E2A47]/70 hover:text-[#0E2A47] font-body"><ArrowLeft size={14} /> Dashboard</Link>
          <div className="font-display font-medium text-[#0E2A47]">Guide content editor</div>
          <button data-testid="content-save" onClick={save} disabled={saving} className="ml-auto inline-flex items-center gap-2 rounded-full bg-[#1FB6C9] text-white px-6 py-2.5 font-display text-sm shadow disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save changes
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-10">
        {/* Wi-Fi */}
        <section className="rounded-2xl bg-white border border-[#0E2A47]/8 p-6">
          <div className="font-display text-xl text-[#0E2A47]">Wi-Fi</div>
          <p className="mt-1 text-sm text-[#0E2A47]/60 font-body">Leave blank to use the default from the code.</p>
          <div className="mt-5 grid md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-[#0E2A47]/50 font-body">Network name (SSID)</span>
              <input data-testid="content-wifi-ssid" value={wifiSsid} onChange={(e) => setWifiSsid(e.target.value)} placeholder={property.wifi_ssid || ""} className="mt-2 w-full rounded-lg border border-[#0E2A47]/15 bg-[#FAFAF8] px-4 py-3 outline-none focus:border-[#1FB6C9]" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-[#0E2A47]/50 font-body">Password</span>
              <input data-testid="content-wifi-password" value={wifiPwd} onChange={(e) => setWifiPwd(e.target.value)} placeholder={property.wifi_password || ""} className="mt-2 w-full rounded-lg border border-[#0E2A47]/15 bg-[#FAFAF8] px-4 py-3 outline-none focus:border-[#1FB6C9]" />
            </label>
          </div>
        </section>

        {/* Sections */}
        {Object.entries(grouped).map(([group, groupSections]) => (
          <section key={group}>
            <h2 className="font-display text-2xl text-[#0E2A47] border-b border-[#0E2A47]/10 pb-3 mb-4">{group}</h2>
            <div className="space-y-4">
              {groupSections.map((s) => {
                const override = sectionOverrides[s.id];
                const value = override ?? s.body;
                const isOverridden = override !== undefined && override !== s.body;
                return (
                  <div key={s.id} data-testid={`content-section-${s.id}`} className="rounded-xl bg-white border border-[#0E2A47]/8 p-5">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="font-display text-lg text-[#0E2A47]">{s.title}</div>
                      <div className="flex items-center gap-2">
                        {isOverridden && <span className="text-xs uppercase tracking-widest text-[#1FB6C9] font-body">Edited</span>}
                        {isOverridden && (
                          <button data-testid={`content-reset-${s.id}`} onClick={() => resetSection(s.id)} className="inline-flex items-center gap-1 text-xs text-[#0E2A47]/60 hover:text-[#0E2A47]"><RotateCcw size={12} /> Revert</button>
                        )}
                      </div>
                    </div>
                    <textarea
                      data-testid={`content-body-${s.id}`}
                      value={value}
                      onChange={(e) => setBody(s.id, e.target.value)}
                      rows={Math.min(Math.max(4, (value.match(/\n/g)?.length || 0) + 2), 20)}
                      className="mt-3 w-full rounded-lg border border-[#0E2A47]/12 bg-[#FAFAF8] px-4 py-3 font-body text-[#0E2A47] outline-none focus:border-[#1FB6C9] leading-relaxed whitespace-pre-wrap"
                    />
                  </div>
                );
              })}
            </div>
          </section>
        ))}

        {/* Extra local recs */}
        <section>
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl text-[#0E2A47]">Extra local recommendations</h2>
            <button data-testid="content-add-extra" onClick={addExtra} className="inline-flex items-center gap-1.5 rounded-full bg-[#0E2A47] text-white text-sm px-4 py-2 font-body"><Plus size={14} /> Add</button>
          </div>
          <p className="mt-2 text-sm text-[#0E2A47]/60 font-body">Add your own restaurant, activity, or park recs — they appear inside the guide's Local guide group.</p>
          <div className="mt-4 space-y-3">
            {extras.length === 0 && <div className="rounded-lg bg-white border border-dashed border-[#0E2A47]/15 p-6 text-center text-[#0E2A47]/60 font-body">No custom recs yet.</div>}
            {extras.map((e, i) => (
              <div key={i} data-testid={`content-extra-${i}`} className="rounded-xl bg-white border border-[#0E2A47]/8 p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <input data-testid={`content-extra-title-${i}`} value={e.title} onChange={(ev) => updExtra(i, "title", ev.target.value)} placeholder="Title (e.g., Gruene Hall)" className="flex-1 rounded-lg border border-[#0E2A47]/15 bg-[#FAFAF8] px-4 py-2 outline-none focus:border-[#1FB6C9] font-body" />
                  <button data-testid={`content-extra-delete-${i}`} onClick={() => delExtra(i)} className="p-2 rounded-lg text-red-500 hover:bg-red-50"><Trash2 size={16} /></button>
                </div>
                <textarea data-testid={`content-extra-body-${i}`} value={e.body} onChange={(ev) => updExtra(i, "body", ev.target.value)} placeholder="Body — what to know, hours, why it's great." rows={3} className="w-full rounded-lg border border-[#0E2A47]/15 bg-[#FAFAF8] px-4 py-3 outline-none focus:border-[#1FB6C9] font-body resize-none" />
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
